/**
 * SupportConsole - User management and support tools
 * Phase 11.5b - Support Console
 */
import React, { useState, useCallback, useEffect } from 'react'
import {
  IoSearch,
  IoPerson,
  IoMail,
  IoShield,
  IoCreate,
  IoTrash,
  IoPricetag,
  IoTime,
  IoCheckmarkCircle,
  IoCloseCircle,
  IoDownload,
  IoWarning,
  IoDocument,
} from 'react-icons/io5'
import { apiGetAuth, apiPostAuth, apiPutAuth, apiDeleteAuth } from '../../api'
import { AccordionSection } from './AccordionSection'
import { CompactCard, CompactCardGrid } from './CompactCard'

interface UserSearchResult {
  id: number
  email: string
  clerk_id: string | null
  is_active: boolean
  is_superuser: boolean
  role: string | null
  created_at: string
  updated_at: string
  tags: string[]
}

interface UserDetail extends UserSearchResult {
  notes_count: number
  recent_activity: Array<{
    id: number
    action: string
    details: Record<string, unknown> | null
    created_at: string
  }>
}

interface UserNote {
  id: number
  admin_id: number | null
  admin_email: string | null
  content: string
  is_pinned: boolean
  created_at: string
  updated_at: string
}

interface UserTag {
  id: number
  name: string
  color: string
  description: string | null
  user_count: number
}

interface GDPRDataSummary {
  user_id: number
  email: string
  account_created: string
  data_counts: {
    tracked_bets: number
    bet_selections: number
    saved_builders: number
    player_shortlists: number
    fixture_notes: number
    alerts: number
    activity_records: number
    admin_notes: number
  }
  has_preferences: boolean
  has_alert_preferences: boolean
}

export function SupportConsole() {
  const [searchQuery, setSearchQuery] = useState('')
  const [users, setUsers] = useState<UserSearchResult[]>([])
  const [selectedUser, setSelectedUser] = useState<UserDetail | null>(null)
  const [userNotes, setUserNotes] = useState<UserNote[]>([])
  const [tags, setTags] = useState<UserTag[]>([])
  const [loading, setLoading] = useState(false)
  const [newNote, setNewNote] = useState('')

  // GDPR state
  const [gdprSummary, setGdprSummary] = useState<GDPRDataSummary | null>(null)
  const [gdprLoading, setGdprLoading] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleteReason, setDeleteReason] = useState('')
  const [deleteImmediate, setDeleteImmediate] = useState(false)

  // Search users
  const searchUsers = useCallback(async () => {
    if (!searchQuery.trim()) return
    setLoading(true)
    try {
      const response = await apiGetAuth<{ users: UserSearchResult[] }>(
        `/support/users?q=${encodeURIComponent(searchQuery)}&limit=20`
      )
      setUsers(response?.users || [])
    } catch (error) {
      console.error('Error searching users:', error)
    } finally {
      setLoading(false)
    }
  }, [searchQuery])

  // Load tags on mount
  useEffect(() => {
    const loadTags = async () => {
      try {
        const response = await apiGetAuth<{ tags: UserTag[] }>('/support/tags')
        setTags(response?.tags || [])
      } catch (error) {
        console.error('Error loading tags:', error)
      }
    }
    loadTags()
  }, [])

  // Load user detail
  const loadUserDetail = useCallback(async (userId: number) => {
    setLoading(true)
    try {
      const [userDetail, notesResponse] = await Promise.all([
        apiGetAuth<UserDetail>(`/support/users/${userId}`),
        apiGetAuth<{ notes: UserNote[] }>(`/support/users/${userId}/notes`),
      ])
      setSelectedUser(userDetail)
      setUserNotes(notesResponse?.notes || [])
    } catch (error) {
      console.error('Error loading user detail:', error)
    } finally {
      setLoading(false)
    }
  }, [])

  // Add note
  const addNote = useCallback(async () => {
    if (!selectedUser || !newNote.trim()) return
    try {
      await apiPostAuth(`/support/users/${selectedUser.id}/notes`, {
        content: newNote,
        is_pinned: false,
      })
      setNewNote('')
      loadUserDetail(selectedUser.id)
    } catch (error) {
      console.error('Error adding note:', error)
    }
  }, [selectedUser, newNote, loadUserDetail])

  // Toggle user active status
  const toggleUserStatus = useCallback(async () => {
    if (!selectedUser) return
    try {
      const endpoint = selectedUser.is_active
        ? `/support/users/${selectedUser.id}/deactivate`
        : `/support/users/${selectedUser.id}/activate`
      await apiPutAuth(endpoint, {})
      loadUserDetail(selectedUser.id)
    } catch (error) {
      console.error('Error toggling user status:', error)
    }
  }, [selectedUser, loadUserDetail])

  // Assign tag to user
  const assignTag = useCallback(async (tagId: number) => {
    if (!selectedUser) return
    try {
      await apiPostAuth(`/support/users/${selectedUser.id}/tags/${tagId}`, {})
      loadUserDetail(selectedUser.id)
    } catch (error) {
      console.error('Error assigning tag:', error)
    }
  }, [selectedUser, loadUserDetail])

  // GDPR: Load data summary
  const loadGDPRSummary = useCallback(async () => {
    if (!selectedUser) return
    setGdprLoading(true)
    try {
      const summary = await apiGetAuth<GDPRDataSummary>(
        `/support/users/${selectedUser.id}/gdpr/summary`
      )
      setGdprSummary(summary)
    } catch (error) {
      console.error('Error loading GDPR summary:', error)
    } finally {
      setGdprLoading(false)
    }
  }, [selectedUser])

  // GDPR: Export user data
  const exportUserData = useCallback(async () => {
    if (!selectedUser) return
    setGdprLoading(true)
    try {
      const data = await apiGetAuth<Record<string, unknown>>(
        `/support/users/${selectedUser.id}/gdpr/export`
      )
      // Download as JSON file
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `gdpr-export-user-${selectedUser.id}-${new Date().toISOString().split('T')[0]}.json`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error exporting user data:', error)
      alert('Failed to export user data. Check console for details.')
    } finally {
      setGdprLoading(false)
    }
  }, [selectedUser])

  // GDPR: Delete/schedule deletion
  const handleDeleteUser = useCallback(async () => {
    if (!selectedUser) return
    setGdprLoading(true)
    try {
      const result = await apiPostAuth<{
        success: boolean
        action: string
        message: string
      }>(`/support/users/${selectedUser.id}/gdpr/delete`, {
        reason: deleteReason || 'GDPR deletion request',
        immediate: deleteImmediate,
      })
      alert(result?.message || 'Operation completed')
      setShowDeleteConfirm(false)
      setDeleteReason('')
      setDeleteImmediate(false)
      if (deleteImmediate) {
        // User was deleted, clear selection
        setSelectedUser(null)
        setGdprSummary(null)
        // Re-run search to update list
        searchUsers()
      } else {
        // User scheduled for deletion, reload their details
        loadUserDetail(selectedUser.id)
      }
    } catch (error) {
      console.error('Error deleting user:', error)
      alert('Failed to delete user. Check console for details.')
    } finally {
      setGdprLoading(false)
    }
  }, [selectedUser, deleteReason, deleteImmediate, searchUsers, loadUserDetail])

  // GDPR: Cancel scheduled deletion
  const cancelDeletion = useCallback(async () => {
    if (!selectedUser) return
    setGdprLoading(true)
    try {
      const result = await apiPostAuth<{ success: boolean; message: string }>(
        `/support/users/${selectedUser.id}/gdpr/cancel-deletion`,
        {}
      )
      alert(result?.message || 'Deletion cancelled')
      loadUserDetail(selectedUser.id)
    } catch (error) {
      console.error('Error cancelling deletion:', error)
      alert('Failed to cancel deletion. Check console for details.')
    } finally {
      setGdprLoading(false)
    }
  }, [selectedUser, loadUserDetail])

  // Load GDPR summary when user is selected
  useEffect(() => {
    if (selectedUser) {
      loadGDPRSummary()
    } else {
      setGdprSummary(null)
    }
  }, [selectedUser, loadGDPRSummary])

  return (
    <div className="support-console">
      {/* User Search */}
      <AccordionSection title="User Search" defaultOpen={true}>
        <div className="support-search">
          <div className="support-search-input">
            <IoSearch size={18} />
            <input
              type="text"
              placeholder="Search by email, user ID, or clerk ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && searchUsers()}
            />
            <button onClick={searchUsers} disabled={loading}>
              Search
            </button>
          </div>

          {users.length > 0 && (
            <div className="support-user-list">
              {users.map((user) => (
                <div
                  key={user.id}
                  className={`support-user-item ${selectedUser?.id === user.id ? 'selected' : ''}`}
                  onClick={() => loadUserDetail(user.id)}
                >
                  <div className="support-user-item-main">
                    <IoPerson size={16} />
                    <span className="support-user-email">{user.email}</span>
                    <span className="support-user-id">#{user.id}</span>
                  </div>
                  <div className="support-user-item-meta">
                    {user.tags.map((tag) => (
                      <span key={tag} className="support-tag-badge">{tag}</span>
                    ))}
                    <span className={`support-status-badge ${user.is_active ? 'active' : 'inactive'}`}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                    {user.role && user.role !== 'user' && (
                      <span className="support-role-badge">{user.role}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </AccordionSection>

      {/* User Detail */}
      {selectedUser && (
        <AccordionSection title={`User: ${selectedUser.email}`} defaultOpen={true}>
          <div className="support-user-detail">
            {/* User Info Cards */}
            <CompactCardGrid columns={4}>
              <CompactCard
                label="User ID"
                value={`#${selectedUser.id}`}
                icon={<IoPerson size={20} />}
                status="neutral"
              />
              <CompactCard
                label="Status"
                value={selectedUser.is_active ? 'Active' : 'Inactive'}
                icon={selectedUser.is_active ? <IoCheckmarkCircle size={20} /> : <IoCloseCircle size={20} />}
                status={selectedUser.is_active ? 'healthy' : 'error'}
              />
              <CompactCard
                label="Role"
                value={selectedUser.role || 'user'}
                icon={<IoShield size={20} />}
                status={selectedUser.is_superuser ? 'warning' : 'neutral'}
              />
              <CompactCard
                label="Notes"
                value={selectedUser.notes_count}
                icon={<IoCreate size={20} />}
                status="neutral"
              />
            </CompactCardGrid>

            {/* Actions */}
            <div className="support-actions">
              <button
                className={`support-action-btn ${selectedUser.is_active ? 'danger' : 'success'}`}
                onClick={toggleUserStatus}
              >
                {selectedUser.is_active ? 'Deactivate User' : 'Activate User'}
              </button>
            </div>

            {/* Tags */}
            <div className="support-tags-section">
              <h4>Tags</h4>
              <div className="support-tags-list">
                {tags.map((tag) => (
                  <button
                    key={tag.id}
                    className="support-tag-btn"
                    style={{ borderColor: tag.color }}
                    onClick={() => assignTag(tag.id)}
                  >
                    <IoPricetag size={14} style={{ color: tag.color }} />
                    {tag.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Recent Activity */}
            <div className="support-activity-section">
              <h4>Recent Activity</h4>
              {selectedUser.recent_activity.length > 0 ? (
                <div className="support-activity-list">
                  {selectedUser.recent_activity.map((activity) => (
                    <div key={activity.id} className="support-activity-item">
                      <IoTime size={14} />
                      <span className="support-activity-action">{activity.action}</span>
                      <span className="support-activity-time">
                        {new Date(activity.created_at).toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="support-empty">No recent activity recorded.</p>
              )}
            </div>

            {/* Notes */}
            <div className="support-notes-section">
              <h4>Admin Notes</h4>
              <div className="support-notes-input">
                <textarea
                  placeholder="Add a note about this user..."
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  rows={3}
                />
                <button onClick={addNote} disabled={!newNote.trim()}>
                  Add Note
                </button>
              </div>
              {userNotes.length > 0 ? (
                <div className="support-notes-list">
                  {userNotes.map((note) => (
                    <div key={note.id} className={`support-note ${note.is_pinned ? 'pinned' : ''}`}>
                      <div className="support-note-header">
                        <span className="support-note-admin">{note.admin_email || 'Unknown'}</span>
                        <span className="support-note-time">
                          {new Date(note.created_at).toLocaleString()}
                        </span>
                      </div>
                      <div className="support-note-content">{note.content}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="support-empty">No notes yet.</p>
              )}
            </div>

            {/* GDPR Section */}
            <div className="support-gdpr-section">
              <h4>
                <IoShield size={16} />
                GDPR Data Management
              </h4>

              {/* Data Summary */}
              {gdprSummary && (
                <div className="gdpr-data-summary">
                  <div className="gdpr-summary-header">
                    <span>Data Stored for This User</span>
                    <span className="gdpr-account-age">
                      Account created: {new Date(gdprSummary.account_created).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="gdpr-data-counts">
                    <div className="gdpr-count-item">
                      <span className="gdpr-count-value">{gdprSummary.data_counts.tracked_bets}</span>
                      <span className="gdpr-count-label">Tracked Bets</span>
                    </div>
                    <div className="gdpr-count-item">
                      <span className="gdpr-count-value">{gdprSummary.data_counts.saved_builders}</span>
                      <span className="gdpr-count-label">Saved Builders</span>
                    </div>
                    <div className="gdpr-count-item">
                      <span className="gdpr-count-value">{gdprSummary.data_counts.player_shortlists}</span>
                      <span className="gdpr-count-label">Shortlists</span>
                    </div>
                    <div className="gdpr-count-item">
                      <span className="gdpr-count-value">{gdprSummary.data_counts.fixture_notes}</span>
                      <span className="gdpr-count-label">Notes</span>
                    </div>
                    <div className="gdpr-count-item">
                      <span className="gdpr-count-value">{gdprSummary.data_counts.alerts}</span>
                      <span className="gdpr-count-label">Alerts</span>
                    </div>
                    <div className="gdpr-count-item">
                      <span className="gdpr-count-value">{gdprSummary.data_counts.activity_records}</span>
                      <span className="gdpr-count-label">Activities</span>
                    </div>
                  </div>
                </div>
              )}

              {/* GDPR Actions */}
              <div className="gdpr-actions">
                <button
                  className="gdpr-action-btn export"
                  onClick={exportUserData}
                  disabled={gdprLoading}
                >
                  <IoDownload size={16} />
                  Export Data (JSON)
                </button>

                {!selectedUser.is_active ? (
                  <button
                    className="gdpr-action-btn cancel-delete"
                    onClick={cancelDeletion}
                    disabled={gdprLoading}
                  >
                    <IoCheckmarkCircle size={16} />
                    Cancel Deletion
                  </button>
                ) : (
                  <button
                    className="gdpr-action-btn delete"
                    onClick={() => setShowDeleteConfirm(true)}
                    disabled={gdprLoading}
                  >
                    <IoTrash size={16} />
                    Request Deletion
                  </button>
                )}
              </div>

              {/* Delete Confirmation Modal */}
              {showDeleteConfirm && (
                <div className="gdpr-delete-modal">
                  <div className="gdpr-delete-modal-content">
                    <h5>
                      <IoWarning size={20} />
                      Confirm Account Deletion
                    </h5>
                    <p className="gdpr-delete-warning">
                      This action will delete all user data including tracked bets, saved builders,
                      shortlists, notes, alerts, and activity history.
                    </p>

                    <div className="gdpr-delete-form">
                      <label>
                        Reason for deletion:
                        <input
                          type="text"
                          placeholder="e.g., User GDPR request, Account cleanup..."
                          value={deleteReason}
                          onChange={(e) => setDeleteReason(e.target.value)}
                        />
                      </label>

                      <label className="gdpr-immediate-checkbox">
                        <input
                          type="checkbox"
                          checked={deleteImmediate}
                          onChange={(e) => setDeleteImmediate(e.target.checked)}
                        />
                        <span>
                          Delete immediately (no 14-day grace period)
                        </span>
                      </label>

                      {deleteImmediate && (
                        <div className="gdpr-immediate-warning">
                          <IoWarning size={14} />
                          Immediate deletion is irreversible. The user will not be able to recover their account.
                        </div>
                      )}
                    </div>

                    <div className="gdpr-delete-actions">
                      <button
                        className="gdpr-cancel-btn"
                        onClick={() => {
                          setShowDeleteConfirm(false)
                          setDeleteReason('')
                          setDeleteImmediate(false)
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        className={`gdpr-confirm-btn ${deleteImmediate ? 'danger' : ''}`}
                        onClick={handleDeleteUser}
                        disabled={gdprLoading}
                      >
                        {deleteImmediate ? 'Delete Permanently' : 'Schedule Deletion'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </AccordionSection>
      )}
    </div>
  )
}
